import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShieldCheck, ShieldX, ShieldAlert, Clock, RefreshCw, User } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { api } from '../App';

const LOGO = 'https://customer-assets-0z36b82j.emergentagent.net/job_notts-gujarati/artifacts/8ofk94lm_GS_logo.webp';
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_STYLES = {
  ACTIVE: { icon: ShieldCheck, ring: 'border-[hsl(var(--lux-gold))]', bg: 'bg-[hsl(var(--lux-royal))]', text: 'text-white', label: 'MEMBERSHIP VERIFIED' },
  PROVISIONAL: { icon: Clock, ring: 'border-amber-500', bg: 'bg-amber-600', text: 'text-white', label: 'PROVISIONAL — NOT YET VERIFIED' },
  ON_HOLD: { icon: ShieldAlert, ring: 'border-amber-600', bg: 'bg-amber-700', text: 'text-white', label: 'VERIFICATION ON HOLD' },
  VERIFICATION_EXPIRED: { icon: ShieldX, ring: 'border-red-700', bg: 'bg-red-800', text: 'text-white', label: 'VERIFICATION EXPIRED' },
  EXPIRED: { icon: Clock, ring: 'border-amber-500', bg: 'bg-amber-600', text: 'text-white', label: 'MEMBERSHIP EXPIRED' },
  SUSPENDED: { icon: ShieldAlert, ring: 'border-amber-600', bg: 'bg-amber-700', text: 'text-white', label: 'MEMBERSHIP SUSPENDED' },
  CANCELLED: { icon: ShieldX, ring: 'border-red-700', bg: 'bg-red-800', text: 'text-white', label: 'MEMBERSHIP CANCELLED' },
  INVALID: { icon: ShieldX, ring: 'border-red-700', bg: 'bg-red-800', text: 'text-white', label: 'INVALID — NOT A RECOGNISED MEMBERSHIP' },
};

export default function VerifyMember() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setData(await api.get(`/api/verify/member/${token}`)); }
    catch (e) { setError(e.message || 'Could not verify'); }
    setLoading(false);
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const status = data?.status || 'INVALID';
  const st = STATUS_STYLES[status] || STATUS_STYLES.INVALID;
  const Icon = st.icon;

  return (
    <div className="min-h-[70vh] bg-[hsl(var(--lux-ivory))] py-10 px-4" data-testid="verify-member-page">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-6">
          <img src={LOGO} alt="Gujarat Samaj Nottingham" className="w-24 h-24 mx-auto rounded-full shadow-md" />
          <h1 className="mt-3 text-2xl font-bold text-[hsl(var(--lux-royal))]" style={{ fontFamily: 'Cormorant Garamond' }}>Gujarat Samaj Nottingham</h1>
          <p className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--lux-gold))]">Membership Verification</p>
        </div>

        {loading ? (
          <Card className="border-2"><CardContent className="p-6 space-y-3"><Skeleton className="h-8 w-2/3" /><Skeleton className="h-5 w-1/2" /><Skeleton className="h-5 w-1/3" /></CardContent></Card>
        ) : error ? (
          <Card className="border-2 border-red-700"><CardContent className="p-6 text-center">
            <p className="font-semibold text-red-800" data-testid="verify-member-error">{error}</p>
            <Button className="mt-4" variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-2" /> Retry</Button>
          </CardContent></Card>
        ) : (
          <Card className={`border-2 ${st.ring} overflow-hidden shadow-xl`} data-testid="verify-member-card">
            <div className={`${st.bg} ${st.text} px-6 py-5 flex items-center gap-3`}>
              <Icon className="w-8 h-8 shrink-0" />
              <div>
                <div className="text-xs tracking-widest opacity-80">{status === 'ACTIVE' ? '✓' : '!'} STATUS</div>
                <div className="font-bold text-lg leading-tight" data-testid="verify-member-status">{st.label}</div>
              </div>
            </div>
            <CardContent className="p-6">
              {status === 'INVALID' ? (
                <p className="text-[hsl(var(--lux-ink)/0.8)]">This QR code does not correspond to a recognised Gujarat Samaj Nottingham membership. Please ask the member for an alternative form of identification.</p>
              ) : (
                <div className="flex gap-5">
                  {data.has_photo ? (
                    <img src={`${BACKEND}/api/verify/member/${token}/photo`} alt="Member" className="w-24 h-32 object-cover rounded-lg border-2 border-[hsl(var(--lux-gold))]" data-testid="verify-member-photo" />
                  ) : (
                    <div className="w-24 h-32 rounded-lg border-2 border-[hsl(var(--lux-gold)/0.4)] bg-[hsl(var(--lux-royal-soft))] flex items-center justify-center"><User className="w-8 h-8 text-[hsl(var(--lux-royal)/0.5)]" /></div>
                  )}
                  <dl className="flex-1 space-y-2 text-sm">
                    <div><dt className="text-[hsl(var(--lux-ink)/0.6)]">Name</dt><dd className="font-semibold text-lg text-[hsl(var(--lux-royal))]" data-testid="verify-member-name">{data.full_name}</dd></div>
                    <div><dt className="text-[hsl(var(--lux-ink)/0.6)]">Membership Number</dt><dd className="font-mono font-bold tracking-wide" data-testid="verify-member-number">{data.membership_number}</dd></div>
                    <div><dt className="text-[hsl(var(--lux-ink)/0.6)]">Membership Type</dt><dd className="font-medium" data-testid="verify-member-type">{data.membership_type}</dd></div>
                    <div><dt className="text-[hsl(var(--lux-ink)/0.6)]">Membership Status</dt><dd className="font-medium">{status}</dd></div>
                    <div><dt className="text-[hsl(var(--lux-ink)/0.6)]">Expiry</dt><dd className="font-medium" data-testid="verify-member-expiry">{data.no_expiry ? 'Lifetime — no expiry' : data.expires_at}</dd></div>
                  </dl>
                </div>
              )}
              {data.note && <p className="mt-4 text-sm p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900" data-testid="verify-member-note">{data.note}</p>}
              {data.is_child && (
                <div className="mt-4 rounded-lg border border-[hsl(var(--lux-gold)/0.5)] bg-[hsl(var(--lux-ivory))] p-4 text-sm" data-testid="verify-member-child">
                  <div className="font-semibold text-[hsl(var(--lux-royal))] mb-1">Child member (under 14)</div>
                  {data.parent_contact ? (
                    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1" data-testid="verify-member-parent-contact">
                      <dt className="text-[hsl(var(--lux-ink)/0.6)]">Parent / guardian</dt><dd className="font-medium">{data.parent_contact.full_name}</dd>
                      {data.parent_contact.phone && <><dt className="text-[hsl(var(--lux-ink)/0.6)]">Phone</dt><dd><a href={`tel:${data.parent_contact.phone}`} className="text-[hsl(var(--lux-royal))] underline" data-testid="verify-member-parent-phone">{data.parent_contact.phone}</a></dd></>}
                      {data.parent_contact.email && <><dt className="text-[hsl(var(--lux-ink)/0.6)]">Email</dt><dd><a href={`mailto:${data.parent_contact.email}`} className="text-[hsl(var(--lux-royal))] underline break-all">{data.parent_contact.email}</a></dd></>}
                      <dt className="text-[hsl(var(--lux-ink)/0.6)]">Membership</dt><dd className="font-mono text-xs">{data.parent_contact.membership_number} · {data.parent_contact.status}</dd>
                    </dl>
                  ) : <p className="text-[hsl(var(--lux-ink)/0.7)]">Parent or guardian details are held by the Samaj office.</p>}
                </div>
              )}
              <p className="mt-5 text-[11px] text-[hsl(var(--lux-ink)/0.55)]">Checked live against the membership register at {new Date(data.checked_at || Date.now()).toLocaleString('en-GB')}. Status can change at any time; always scan rather than relying on a saved image.</p>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" size="sm" onClick={load} data-testid="verify-member-refresh"><RefreshCw className="w-4 h-4 mr-2" /> Re-check</Button>
                <Link to="/"><Button variant="ghost" size="sm">Visit website</Button></Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
